import type { Class, ProjectKind } from '../data/types';
import { BACKGROUND_NOISE_RECORD_SECONDS, DEFAULT_RECORD_SECONDS } from '../ml/audioFeatures';
import { isBackgroundNoiseClass } from '../ml/audioTrainer';
import { DEFAULT_HOLD_FPS } from './WebcamPanel';

/**
 * Pure helpers for the per-class capture-settings dialog. Lives in its
 * own file so the dialog component stays HMR-friendly (react-refresh
 * complains when a component file also exports non-components).
 */

export interface ResolvedCaptureSettings {
  delaySeconds: number;
  durationSeconds: number;
  fps: number;
  holdToRecord: boolean;
}

/** Defaults the dialog falls back to per project-kind. The audio
 *  Background-noise class records 20s by default, every other class
 *  records 2s. Webcam (image + pose) uses DEFAULT_HOLD_FPS — currently
 *  24, matching Teachable Machine — and press-and-hold gesture. Audio
 *  also stores fps even though the audio capture path doesn't read it,
 *  so the gear-icon dialog has a single shape across kinds. */
export function defaultSettingsFor(
  cls: Pick<Class, 'name'>,
  kind: ProjectKind,
): ResolvedCaptureSettings {
  if (kind === 'audio') {
    return {
      delaySeconds: 0,
      durationSeconds: isBackgroundNoiseClass(cls)
        ? BACKGROUND_NOISE_RECORD_SECONDS
        : DEFAULT_RECORD_SECONDS,
      fps: DEFAULT_HOLD_FPS,
      holdToRecord: true,
    };
  }
  return {
    // 2-second pre-record countdown by default — matches Teachable
    // Machine and gives the kid a beat to step into frame and pose
    // before the burst starts. They can still set it to 0 in the
    // gear-icon dialog.
    delaySeconds: 2,
    durationSeconds: 6,
    fps: DEFAULT_HOLD_FPS,
    holdToRecord: true,
  };
}

/** Read effective capture settings for a class — class overrides win
 *  over the kind defaults. */
export function effectiveSettings(cls: Class, kind: ProjectKind): ResolvedCaptureSettings {
  return { ...defaultSettingsFor(cls, kind), ...(cls.captureSettings ?? {}) };
}
